import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { IS_AWS_MODE } from '../config/frontend';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();

  if (isAuthenticated) {
    // Redirect based on user role
    if (!IS_AWS_MODE && user?.role === 'superadmin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('auth:loginError'));
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password, otp || undefined);
      
      // Handle different response types
      if (result.id_token || result.access_token) {
        // Success - user is now logged in, will redirect via isAuthenticated check
        return;
      }
      
      if (result.code === 'TOTP_SETUP_REQUIRED' && result.temp_token) {
        // Save temp token and redirect to TOTP setup
        sessionStorage.setItem('totp_temp_token', result.temp_token);
        navigate('/auth/totp-setup');
        return;
      }
      
      if (result.code === 'TOTP_REQUIRED') {
        // Show OTP field and prompt for code
        setShowOtp(true);
        toast.error(t('errors:totp_required'));
        return;
      }
      
      if (result && typeof result === 'object' && 'message' in result && 
          typeof result.message === 'string' && result.message.includes('password change')) {
        // Password change required
        navigate('/auth/change-password');
        return;
      }
      
      // Other error
      toast.error(result.message || t('auth:loginError'));
      
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || t('auth:loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Language Switcher */}
        <div className="flex justify-end">
          <LanguageSwitcher compact />
        </div>

        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <BrandLogo size="xl" />
          </div>
          <h1 className="text-heading-1 text-[var(--text-primary)] mb-2">
            {t('auth:welcome')}
          </h1>
          <p className="text-body text-[var(--text-secondary)]">
            {t('common:tagline')}
          </p>
        </div>

        {/* Demo Credentials Card */}
        {!IS_AWS_MODE && (
        <div className="bg-[var(--surface)] border border-[var(--border-light)] rounded-xl p-4 shadow-soft">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('auth:demoCredentials')}</h3>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">{t('auth:superAdmin')}:</span>
              <span className="font-mono text-[var(--text-primary)]">admin@agrisafe.fr / admin123</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">{t('auth:coopAdmin')}:</span>
              <span className="font-mono text-[var(--text-primary)]">coop@agrisafe.fr / coop123</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">{t('auth:farmer')}:</span>
              <span className="font-mono text-[var(--text-primary)]">farmer@agrisafe.fr / farmer123</span>
            </div>
          </div>
        </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {t('auth:emailLabel')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder={t('auth:emailPlaceholder')}
                data-testid="email-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {t('auth:passwordLabel')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder={t('auth:passwordPlaceholder')}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  data-testid="password-toggle"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* OTP Field (shown when required) */}
            {showOtp && (
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4" />
                    <span>{t('auth:totp.verificationCode')}</span>
                  </div>
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input-field text-center text-lg font-mono tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  data-testid="otp-input"
                />
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  {t('auth:totp.enterCode')}
                </p>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('auth:signingIn')}</span>
              </>
            ) : (
              <span>{t('auth:signIn')}</span>
            )}
          </button>
        </form>

        {/* Safety Notice */}
        <div className="bg-gradient-accent bg-opacity-10 border border-[var(--color-warning)] rounded-lg p-4 text-center">
          <p className="text-sm text-[var(--color-warning)] font-medium mb-1">
            🔒 {t('auth:safetyNotice')}
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            {t('auth:safetyNoticeDesc')}
          </p>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-[var(--text-tertiary)]">
            {t('auth:footer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
